import React from "react";
import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import styles from "./AIAssistant.module.css";

const API_URL = "https://taskpro.fooderp.in/api/ai";

// Temporary ERP user ID
// Replace this with the logged-in ERP user ID.
// Get logged-in ERP user ID from localStorage


// ======================================================
// USER ID FROM URL / SESSION STORAGE
// ======================================================

function getUserId() {
    const path = window.location.pathname;

    // Example:
    // http://localhost:5173/user=123

    const match = path.match(
        /^\/user=(\d+)$/
    );

    if (match) {
        const userId = match[1];

        sessionStorage.setItem(
            "user_id",
            userId
        );

        return Number(userId);
    }

    // Get previously stored user ID
    const storedUserId =
        sessionStorage.getItem("Default_EmployeeID");

    if (storedUserId) {
        return Number(storedUserId);
    }

    // Temporary fallback
    sessionStorage.setItem(
        "user_id",
        "123"
    );

    return 123;
}

// const USER_ID = getUserId();

// console.log(
//     "AI Chat USER_ID:",
//     USER_ID
// );
// let storedUserId = localStorage.getItem("user_id");

// if (!storedUserId) {
//   localStorage.setItem("user_id", "123");
//   storedUserId = "123";
// }

// const USER_ID = Number(storedUserId);

// ======================================================
// ICON
// ======================================================

function Icon({ name, size = 18 }) {
    const icons = {
        menu: "☰",
        plus: "＋",
        search: "⌕",
        chat: "◌",
        more: "⋯",
        trash: "⌫",
        edit: "✎",
        send: "↑",
        stop: "■",
        copy: "▣",
        refresh: "↻",
        close: "×",
        spark: "✦",
        database: "▤",
        chart: "▥",
        orders: "▣",
        sun: "☼",
    };

    return (
        <span
            className={`${styles.icon} icon-${name}`}
            style={{
                fontSize: size,
                width: size,
                height: size,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
                flexShrink: 0,
            }}
        >
            {icons[name] || "•"}
        </span>
    );
}


// ======================================================
// MAIN
// ======================================================

function AIAssistant({ isOpen, setIsOpen, login_employeeId }) {


    const USER_ID = login_employeeId

    console.log(
        "AI Chat USER_ID:",
        USER_ID
    );

    const [position, setPosition] = useState({
        x: window.innerWidth - 78,
        y: window.innerHeight - 158,
    });

    const [dragging, setDragging] = useState(false);

    const dragOffset = useRef({
        x: 0,
        y: 0,
    });

    const handleMouseDown = (event) => {
        event.preventDefault();
        setDragging(true);

        dragOffset.current = {
            x: event.clientX - position.x,
            y: event.clientY - position.y,
        };
    };


    const [chats, setChats] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);

    const [input, setInput] = useState("");

    const [loading, setLoading] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [search, setSearch] = useState("");

    const [connection, setConnection] = useState(
        "checking"
    );

    const [menuChatId, setMenuChatId] =
        useState(null);

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    const abortControllerRef = useRef(null);


    useEffect(() => {
        if (!dragging) return;

        const handleMouseMove = (event) => {
            setPosition({
                x: Math.max(
                    0,
                    Math.min(
                        event.clientX - dragOffset.current.x,
                        window.innerWidth - 58
                    )
                ),
                y: Math.max(
                    0,
                    Math.min(
                        event.clientY - dragOffset.current.y,
                        window.innerHeight - 58
                    )
                ),
            });
        };

        const handleMouseUp = () => {
            setDragging(false);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [dragging]);





    // ====================================================
    // INITIAL LOAD
    // ====================================================

    useEffect(() => {
        loadChats();
        checkConnection();
    }, []);


    // ====================================================
    // AUTO SCROLL
    // ====================================================

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({
            behavior: "smooth",
        });
    }, [messages, loading]);


    // ====================================================
    // CLOSE MENU
    // ====================================================

    useEffect(() => {
        function closeMenu() {
            setMenuChatId(null);
        }

        document.addEventListener(
            "click",
            closeMenu
        );

        return () => {
            document.removeEventListener(
                "click",
                closeMenu
            );
        };
    }, []);


    // Show only the floating AI button when the AI page is closed.
    if (!isOpen) {
        return (
            <>
                <div
                    onMouseDown={handleMouseDown}
                    onClick={() => {
                        if (!dragging) {
                            setIsOpen(true);
                        }
                    }}
                    title="AI Assistant"
                    style={{
                        position: "fixed",
                        top: `${position.y}px`,
                        left: `${position.x}px`,
                        width: "58px",
                        height: "58px",
                        borderRadius: "50%",
                        background: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: dragging ? "grabbing" : "grab",
                        zIndex: 2147483647,
                        border: "2px solid #1976d2",
                        boxShadow: dragging
                            ? "0 10px 30px rgba(25,118,210,0.35)"
                            : "0 6px 20px rgba(0,0,0,0.18)",
                        userSelect: "none",
                        touchAction: "none",
                        animation: "aiGlow 2s infinite",
                    }}
                >
                    <span className={styles["ai-spark-colorful"]}>
                        ✦
                    </span>
                </div>

                <style>
                    {`
                @keyframes aiGlow {
                0% {
                    box-shadow: 0 6px 20px rgba(25,118,210,0.18);
                }

                50% {
                    box-shadow: 0 6px 28px rgba(123,31,162,0.40);
                }

                100% {
                    box-shadow: 0 6px 20px rgba(25,118,210,0.18);
                }
                }
            `}
                </style>
            </>
        );
    }

    // ====================================================
    // CONNECTION
    // ====================================================

    async function checkConnection() {
        try {
            const response = await fetch(
                `${API_URL}/health`
            );

            setConnection(
                response.ok ? "online" : "offline"
            );
        } catch {
            setConnection("offline");
        }
    }


    // ====================================================
    // LOAD CHATS
    // ====================================================

    async function loadChats() {
        try {
            const response = await fetch(
                `${API_URL}/api/ai/chats?user_id=${USER_ID}`
            );

            if (!response.ok) {
                throw new Error(
                    "Unable to load chats"
                );
            }

            const data = await response.json();

            const chatList = data.chats || [];

            setChats(chatList);

            if (chatList.length > 0) {
                await openChat(
                    chatList[0].id,
                    chatList
                );
            } else {
                await createNewChat();
            }
        } catch (error) {
            console.error(
                "Load chats:",
                error
            );

            setConnection("offline");
        }
    }


    // ====================================================
    // CREATE CHAT
    // ====================================================

    async function createNewChat() {
        if (loading) return;

        try {
            const response = await fetch(
                `${API_URL}/api/ai/new-chat?user_id=${USER_ID}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    "Failed to create chat"
                );
            }

            const chat =
                await response.json();

            const newChat = {
                id: chat.id,
                title: chat.title,
            };

            setChats((previous) => [
                newChat,
                ...previous.filter(
                    (item) =>
                        item.id !== newChat.id
                ),
            ]);

            setActiveChat(newChat);
            setMessages([]);
            setInput("");

            textareaRef.current?.focus();

            if (
                window.innerWidth < 900
            ) {
                setSidebarOpen(false);
            }
        } catch (error) {
            console.error(
                "Create chat:",
                error
            );
        }
    }


    // ====================================================
    // OPEN CHAT
    // ====================================================

    async function openChat(
        chatId,
        suppliedChats = chats
    ) {
        if (loading) return;

        try {
            const response = await fetch(
                `${API_URL}/api/ai/chats/${chatId}/messages?user_id=${USER_ID}`
            );

            if (!response.ok) {
                throw new Error(
                    "Failed to load chat"
                );
            }

            const data =
                await response.json();

            const chat =
                suppliedChats.find(
                    (item) =>
                        item.id === chatId
                );

            setActiveChat(
                chat || {
                    id: chatId,
                    title: "Chat",
                }
            );

            setMessages(
                (data.messages || []).map(
                    (message) => ({
                        id: message.id,
                        role: message.role,
                        content: message.content,
                    })
                )
            );

            setMenuChatId(null);

            if (
                window.innerWidth < 900
            ) {
                setSidebarOpen(false);
            }
        } catch (error) {
            console.error(
                "Open chat:",
                error
            );
        }
    }


    // ====================================================
    // SEND MESSAGE
    // ====================================================

    async function sendMessage(
        customQuestion = null
    ) {
        const question =
            customQuestion ??
            input.trim();

        if (
            !question ||
            loading ||
            !activeChat
        ) {
            return;
        }

        const userId =
            Date.now();

        const assistantId =
            userId + 1;

        setMessages(
            (previous) => [
                ...previous,

                {
                    id: userId,
                    role: "user",
                    content: question,
                },

                {
                    id: assistantId,
                    role: "assistant",
                    content: "",
                    status: "Thinking...",
                },
            ]
        );

        setInput("");
        setLoading(true);

        if (textareaRef.current) {
            textareaRef.current.style.height =
                "auto";
        }

        const controller =
            new AbortController();

        abortControllerRef.current =
            controller;

        try {
            const response =
                await fetch(
                    `${API_URL}/api/ai/query/stream`,
                    {
                        method: "POST",

                        headers: {
                            "Content-Type":
                                "application/json",

                            Accept:
                                "text/event-stream",
                        },

                        body: JSON.stringify({
                            question,

                            thread_id:
                                activeChat.id,

                            user_id:
                                USER_ID,
                        }),

                        signal:
                            controller.signal,
                    }
                );

            if (!response.ok) {
                let message =
                    `Request failed (${response.status})`;

                try {
                    const data =
                        await response.json();

                    message =
                        data.detail ||
                        message;
                } catch { }

                throw new Error(
                    message
                );
            }

            if (!response.body) {
                throw new Error(
                    "Streaming is not supported."
                );
            }

            const reader =
                response.body.getReader();

            const decoder =
                new TextDecoder(
                    "utf-8"
                );

            let buffer = "";

            while (true) {
                const {
                    value,
                    done,
                } =
                    await reader.read();

                if (done) break;

                buffer +=
                    decoder.decode(
                        value,
                        {
                            stream: true,
                        }
                    );

                const events =
                    buffer.split("\n\n");

                buffer =
                    events.pop() || "";

                for (
                    const event of events
                ) {
                    processSSEEvent(
                        event,
                        assistantId
                    );
                }
            }

            await refreshChats();

        } catch (error) {
            if (
                error.name ===
                "AbortError"
            ) {
                setMessages(
                    (previous) =>
                        previous.map(
                            (message) =>
                                message.id ===
                                    assistantId
                                    ? {
                                        ...message,
                                        status:
                                            undefined,
                                    }
                                    : message
                        )
                );

                return;
            }

            setMessages(
                (previous) =>
                    previous.map(
                        (message) =>
                            message.id ===
                                assistantId
                                ? {
                                    ...message,

                                    content:
                                        `**Error**\n\n${error.message}`,

                                    status:
                                        undefined,
                                }
                                : message
                    )
            );
        } finally {
            setLoading(false);

            abortControllerRef.current =
                null;
        }
    }


    // ====================================================
    // SSE
    // ====================================================

    function processSSEEvent(
        event,
        assistantId
    ) {
        let eventData = "";

        for (
            const line of event.split("\n")
        ) {
            if (
                line.startsWith(
                    "data:"
                )
            ) {
                eventData +=
                    line
                        .substring(5)
                        .trim();
            }
        }

        if (!eventData) return;

        try {
            const data =
                JSON.parse(
                    eventData
                );

            if (
                data.type ===
                "status"
            ) {
                setMessages(
                    (previous) =>
                        previous.map(
                            (message) =>
                                message.id ===
                                    assistantId
                                    ? {
                                        ...message,
                                        status:
                                            data.message,
                                    }
                                    : message
                        )
                );
            }

            if (
                data.type ===
                "token"
            ) {
                setMessages(
                    (previous) =>
                        previous.map(
                            (message) =>
                                message.id ===
                                    assistantId
                                    ? {
                                        ...message,

                                        content:
                                            message.content +
                                            data.content,

                                        status:
                                            undefined,
                                    }
                                    : message
                        )
                );
            }

            if (
                data.type ===
                "done"
            ) {
                setMessages(
                    (previous) =>
                        previous.map(
                            (message) =>
                                message.id ===
                                    assistantId
                                    ? {
                                        ...message,
                                        status:
                                            undefined,
                                    }
                                    : message
                        )
                );
            }

            if (
                data.type ===
                "error"
            ) {
                setMessages(
                    (previous) =>
                        previous.map(
                            (message) =>
                                message.id ===
                                    assistantId
                                    ? {
                                        ...message,

                                        content:
                                            `**Error**\n\n${data.message}`,

                                        status:
                                            undefined,
                                    }
                                    : message
                        )
                );
            }
        } catch (error) {
            console.error(
                "SSE parse:",
                error
            );
        }
    }


    // ====================================================
    // REFRESH CHATS
    // ====================================================

    async function refreshChats() {
        try {
            const response =
                await fetch(
                    `${API_URL}/api/ai/chats?user_id=${USER_ID}`
                );

            if (!response.ok) return;

            const data =
                await response.json();

            const list =
                data.chats || [];

            setChats(list);

            const updated =
                list.find(
                    (chat) =>
                        chat.id ===
                        activeChat?.id
                );

            if (updated) {
                setActiveChat(updated);
            }
        } catch (error) {
            console.error(
                "Refresh chats:",
                error
            );
        }
    }


    // ====================================================
    // STOP
    // ====================================================

    function stopGeneration() {
        abortControllerRef.current?.abort();

        setLoading(false);
    }


    // ====================================================
    // DELETE CHAT
    // ====================================================

    async function deleteChat(
        event,
        chatId
    ) {
        event.stopPropagation();

        setMenuChatId(null);

        const confirmed =
            window.confirm(
                "Delete this conversation?"
            );

        if (!confirmed) return;

        try {
            const response =
                await fetch(
                    `${API_URL}/api/ai/chats/${chatId}?user_id=${USER_ID}`,
                    {
                        method: "DELETE",
                    }
                );

            if (!response.ok) {
                throw new Error(
                    "Delete failed"
                );
            }

            const remaining =
                chats.filter(
                    (chat) =>
                        chat.id !== chatId
                );

            setChats(remaining);

            if (
                activeChat?.id ===
                chatId
            ) {
                if (
                    remaining.length
                ) {
                    await openChat(
                        remaining[0].id,
                        remaining
                    );
                } else {
                    await createNewChat();
                }
            }
        } catch (error) {
            console.error(
                "Delete:",
                error
            );
        }
    }


    // ====================================================
    // RENAME
    // ====================================================

    async function renameChat(
        event,
        chat
    ) {
        event.stopPropagation();

        setMenuChatId(null);

        const title =
            window.prompt(
                "Conversation name",
                chat.title
            );

        if (!title?.trim()) return;

        try {
            const response =
                await fetch(
                    `${API_URL}/api/ai/chats/${chat.id}?user_id=${USER_ID}`,
                    {
                        method: "PATCH",

                        headers: {
                            "Content-Type":
                                "application/json",
                        },

                        body: JSON.stringify({
                            title:
                                title.trim(),
                        }),
                    }
                );

            if (!response.ok) {
                throw new Error(
                    "Rename failed"
                );
            }

            const data =
                await response.json();

            setChats(
                (previous) =>
                    previous.map(
                        (item) =>
                            item.id ===
                                chat.id
                                ? {
                                    ...item,
                                    title:
                                        data.title,
                                }
                                : item
                    )
            );

            if (
                activeChat?.id ===
                chat.id
            ) {
                setActiveChat(
                    (previous) => ({
                        ...previous,
                        title:
                            data.title,
                    })
                );
            }
        } catch (error) {
            console.error(
                "Rename:",
                error
            );
        }
    }


    // ====================================================
    // COPY
    // ====================================================

    async function copyMessage(
        content
    ) {
        try {
            await navigator.clipboard.writeText(
                content
            );
        } catch (error) {
            console.error(
                "Copy failed:",
                error
            );
        }
    }


    // ====================================================
    // REGENERATE
    // ====================================================

    function regenerate() {
        if (loading) return;

        const lastUserMessage =
            [...messages]
                .reverse()
                .find(
                    (message) =>
                        message.role ===
                        "user"
                );

        if (!lastUserMessage) return;

        setMessages(
            (previous) => {
                const lastAssistantIndex =
                    previous
                        .map(
                            (item) =>
                                item.role
                        )
                        .lastIndexOf(
                            "assistant"
                        );

                if (
                    lastAssistantIndex >=
                    0
                ) {
                    return previous.filter(
                        (_, index) =>
                            index !==
                            lastAssistantIndex
                    );
                }

                return previous;
            }
        );

        setTimeout(() => {
            sendMessage(
                lastUserMessage.content
            );
        }, 50);
    }


    // ====================================================
    // TEXTAREA
    // ====================================================

    function handleInput(
        event
    ) {
        const value =
            event.target.value;

        setInput(value);

        event.target.style.height =
            "auto";

        event.target.style.height =
            `${Math.min(
                event.target.scrollHeight,
                180
            )}px`;
    }


    // ====================================================
    // KEYBOARD
    // ====================================================

    function handleKeyDown(
        event
    ) {
        if (
            event.key ===
            "Enter" &&
            !event.shiftKey
        ) {
            event.preventDefault();

            sendMessage();
        }
    }


    // ====================================================
    // SEARCH
    // ====================================================

    const filteredChats =
        chats.filter(
            (chat) =>
                chat.title
                    ?.toLowerCase()
                    .includes(
                        search
                            .toLowerCase()
                    )
        );


    // ====================================================
    // SUGGESTIONS
    // ====================================================

    const suggestions = [
        {
            icon: "orders",
            title: "Today's orders",
            text: "View today's order summary",
            question:
                "Give me a summary of today's orders including total orders, completed orders, pending orders, cancelled orders, and total order value.",
        },

        {
            icon: "chart",
            title: "Today's sales",
            text: "Show today's sales performance",
            question:
                "Show me today's sales summary including total sales, number of orders, average order value, and comparison with yesterday.",
        },

        {
            icon: "database",
            title: "Top products",
            text: "Find my best-selling products",
            question:
                "Show me the top 10 best-selling products based on quantity sold and sales amount.",
        },

        {
            icon: "orders",
            title: "Pending orders",
            text: "Find orders that need attention",
            question:
                "Show me all currently pending and processing orders that need attention.",
        },
    ];


    // ====================================================
    // UI
    // ====================================================

    return (
        <div className={styles.app}>

            {/* ================================================
            SIDEBAR
        ================================================ */}

            <aside
                className={`${styles.sidebar} ${sidebarOpen
                    ? styles["sidebar-open"]
                    : styles["sidebar-closed"]
                    }`}
            >

                <div className={styles["sidebar-top"]}>

                    <div className={styles.brand}>

                        <div className={styles["brand-icon"]}>
                            <span className={styles["ai-spark-colorful"]}>
                                ✦
                            </span>
                        </div>

                        {sidebarOpen && (
                            <div className={styles["brand-text"]}>

                                <div className={styles["brand-name"]}>
                                    FoodERP
                                </div>

                                <div className={styles["brand-subtitle"]}>
                                    AI Assistant
                                </div>

                            </div>
                        )}

                    </div>


                    <button
                        className={styles["collapse-button"]}
                        onClick={() =>
                            setSidebarOpen(
                                !sidebarOpen
                            )
                        }
                        title="Toggle sidebar"
                    >
                        <Icon
                            name="menu"
                            size={18}
                        />
                    </button>

                </div>


                {sidebarOpen && (
                    <>

                        <button
                            className={styles["new-chat"]}
                            onClick={
                                createNewChat
                            }
                            disabled={loading}
                        >

                            <Icon
                                name="plus"
                                size={20}
                            />

                            <span>
                                New chat
                            </span>

                            <kbd>
                                Ctrl K
                            </kbd>

                        </button>


                        <div className={styles["sidebar-search"]}>

                            <Icon
                                name="search"
                                size={18}
                            />

                            <input
                                value={search}
                                onChange={(event) =>
                                    setSearch(
                                        event.target.value
                                    )
                                }
                                placeholder="Search chats"
                            />

                        </div>


                        <div className={styles.history}>

                            <div className={styles["history-heading"]}>
                                Conversations
                            </div>


                            {filteredChats.length ===
                                0 ? (

                                <div className={styles["empty-history"]}>
                                    No conversations found
                                </div>

                            ) : (

                                filteredChats.map(
                                    (chat) => (

                                        <div
                                            key={chat.id}
                                            className={`${styles["history-item"]} ${activeChat?.id ===
                                                chat.id
                                                ? styles.active
                                                : ""
                                                }`}
                                            onClick={() =>
                                                openChat(
                                                    chat.id
                                                )
                                            }
                                        >

                                            <div className={styles["history-chat-icon"]}>
                                                <Icon
                                                    name="chat"
                                                    size={17}
                                                />
                                            </div>


                                            <span className={styles["history-title"]}>
                                                {chat.title}
                                            </span>


                                            <button
                                                className={styles["history-more"]}
                                                onClick={(event) => {
                                                    event.stopPropagation();

                                                    setMenuChatId(
                                                        menuChatId ===
                                                            chat.id
                                                            ? null
                                                            : chat.id
                                                    );
                                                }}
                                            >
                                                <Icon
                                                    name="more"
                                                    size={18}
                                                />
                                            </button>


                                            {menuChatId ===
                                                chat.id && (

                                                    <div
                                                        className={styles["chat-menu"]}
                                                        onClick={(event) =>
                                                            event.stopPropagation()
                                                        }
                                                    >

                                                        <button
                                                            onClick={(event) =>
                                                                renameChat(
                                                                    event,
                                                                    chat
                                                                )
                                                            }
                                                        >
                                                            <Icon
                                                                name="edit"
                                                                size={16}
                                                            />

                                                            Rename
                                                        </button>


                                                        <button
                                                            className={styles.danger}
                                                            onClick={(event) =>
                                                                deleteChat(
                                                                    event,
                                                                    chat.id
                                                                )
                                                            }
                                                        >
                                                            <Icon
                                                                name="trash"
                                                                size={16}
                                                            />

                                                            Delete
                                                        </button>

                                                    </div>

                                                )}

                                        </div>

                                    )
                                )

                            )}

                        </div>


                        <div className={styles["sidebar-bottom"]}>

                            <div className={styles["connection-card"]}>

                                <span
                                    className={`${styles["connection-dot"]} ${styles[connection]}`}
                                />

                                <span>
                                    {connection ===
                                        "online"
                                        ? "Backend connected"
                                        : connection ===
                                            "offline"
                                            ? "Backend offline"
                                            : "Checking..."}
                                </span>

                            </div>

                        </div>

                    </>
                )}

            </aside>


            {/* ================================================
            MAIN
        ================================================ */}

            <main className={styles.main}>


                {/* HEADER */}

                <header className={styles.header}>

                    <div className={styles["header-left"]}>

                        {!sidebarOpen && (
                            <button
                                className={styles["header-menu"]}
                                onClick={() =>
                                    setSidebarOpen(
                                        true
                                    )
                                }
                            >
                                <Icon
                                    name="menu"
                                    size={19}
                                />
                            </button>
                        )}


                        <div>

                            <div className={styles["header-title"]}>

                                {activeChat?.title ||
                                    "FoodERP AI"}

                            </div>

                            <div className={styles["header-subtitle"]}>

                                SQL Business
                                Intelligence

                            </div>

                        </div>

                    </div>


                    <div className={styles["header-right"]}>

                        <div className={styles.online}>

                            <span
                                className={`${styles["online-dot"]} ${styles[connection]}`}
                            />

                            {connection ===
                                "online"
                                ? "Online"
                                : "Offline"}

                        </div>


                        <button
                            className={styles["header-icon-button"]}
                            title="New chat"
                            onClick={
                                createNewChat
                            }
                        >
                            <Icon
                                name="plus"
                                size={19}
                            />
                        </button>

                        <button
                            className={styles["ai-close-button"]}
                            title="Close AI"
                            onClick={() => setIsOpen(false)}
                        >
                            <Icon name="close" size={20} />
                        </button>

                    </div>

                </header>


                {/* CHAT */}

                <section className={styles.chat}>

                    {messages.length ===
                        0 && (

                            <div className={styles.welcome}>

                                <div className={styles["welcome-icon"]}>
                                    <span className={styles["ai-spark-colorful"]}>
                                        ✦
                                    </span>
                                </div>


                                <h1>
                                    How can I help you?
                                </h1>


                                <p>
                                    Ask questions about
                                    your FoodERP data
                                    using natural language.
                                </p>


                                <div className={styles.suggestions}>

                                    {suggestions.map(
                                        (item) => (

                                            <button
                                                key={
                                                    item.title
                                                }
                                                className={styles.suggestion}
                                                onClick={() =>
                                                    sendMessage(
                                                        item.question
                                                    )
                                                }
                                            >

                                                <div className={styles["suggestion-icon"]}>

                                                    <Icon
                                                        name={
                                                            item.icon
                                                        }
                                                        size={19}
                                                    />

                                                </div>


                                                <div className={styles["suggestion-content"]}>

                                                    <strong>
                                                        {item.title}
                                                    </strong>

                                                    <span>
                                                        {item.text}
                                                    </span>

                                                </div>


                                                <span className={styles["suggestion-arrow"]}>
                                                    →
                                                </span>

                                            </button>

                                        )
                                    )}

                                </div>

                            </div>

                        )}


                    {/* ============================================
                MESSAGES
            ============================================ */}

                    <div className={styles["messages-container"]}>

                        {messages.map(
                            (message, index) => (

                                <Message
                                    key={
                                        message.id
                                    }
                                    message={
                                        message
                                    }
                                    index={
                                        index
                                    }
                                    loading={
                                        loading
                                    }
                                    onCopy={
                                        copyMessage
                                    }
                                    onRegenerate={
                                        regenerate
                                    }
                                />

                            )
                        )}

                    </div>


                    <div
                        ref={
                            messagesEndRef
                        }
                    />

                </section>


                {/* ================================================
                INPUT
            ================================================ */}

                <div className={styles["input-area"]}>

                    <div className={styles["input-wrapper"]}>

                        <textarea
                            ref={
                                textareaRef
                            }
                            value={input}
                            onChange={
                                handleInput
                            }
                            onKeyDown={
                                handleKeyDown
                            }
                            placeholder={
                                activeChat
                                    ? "Ask anything about your FoodERP data..."
                                    : "Create a chat to start"
                            }
                            rows={1}
                            disabled={
                                loading ||
                                !activeChat
                            }
                        />


                        <div className={styles["input-bottom"]}>

                            <div className={styles["input-hints"]}>

                                <span>
                                    ↵ Send
                                </span>

                                <span>
                                    ⇧ ↵ New line
                                </span>

                            </div>


                            {loading ? (

                                <button
                                    className={styles["stop-button"]}
                                    onClick={
                                        stopGeneration
                                    }
                                >

                                    <Icon
                                        name="stop"
                                        size={13}
                                    />

                                    Stop

                                </button>

                            ) : (

                                <button
                                    className={styles["send-button"]}
                                    onClick={() =>
                                        sendMessage()
                                    }
                                    disabled={
                                        !input.trim() ||
                                        !activeChat
                                    }
                                >

                                    <Icon
                                        name="send"
                                        size={19}
                                    />

                                </button>

                            )}

                        </div>

                    </div>


                    <div className={styles.disclaimer}>

                        FoodERP AI can make mistakes.
                        Verify important business data.

                    </div>

                </div>

            </main>

        </div>
    );
}


// ======================================================
// MESSAGE COMPONENT
// ======================================================

function Message({
    message,
    index,
    loading,
    onCopy,
    onRegenerate,
}) {
    const isUser =
        message.role === "user";

    const isLast = index === 0;

    return (
        <div
            className={`${styles["message-row"]} ${isUser
                ? styles.user
                : styles.assistant
                }`}
        >

            {!isUser && (
                <div className={styles["ai-avatar"]}>
                    <span className={styles["ai-spark-colorful"]}>
                        ✦
                    </span>
                </div>
            )}


            <div className={styles["message-column"]}>

                <div
                    className={`${styles["message-content"]} ${isUser
                        ? styles.user
                        : styles.assistant
                        }`}
                >

                    {message.status && (
                        <div className={styles.thinking}>

                            <span className={styles["thinking-dots"]}>
                                <i />
                                <i />
                                <i />
                            </span>

                            {message.status}

                        </div>
                    )}


                    {message.content && (
                        <ReactMarkdown
                            remarkPlugins={[
                                remarkGfm,
                            ]}
                            components={{
                                table:
                                    ({
                                        children,
                                    }) => (
                                        <div className={styles["table-wrapper"]}>
                                            <table>
                                                {children}
                                            </table>
                                        </div>
                                    ),

                                code:
                                    ({
                                        inline,
                                        children,
                                    }) =>
                                        inline ? (
                                            <code className={styles["inline-code"]}>
                                                {children}
                                            </code>
                                        ) : (
                                            <pre className={styles["code-block"]}>
                                                <code>
                                                    {children}
                                                </code>
                                            </pre>
                                        ),
                            }}
                        >
                            {message.content}
                        </ReactMarkdown>
                    )}

                </div>


                {!isUser &&
                    message.content &&
                    !message.status && (

                        <div className={styles["message-actions"]}>

                            <button
                                onClick={() =>
                                    onCopy(
                                        message.content
                                    )
                                }
                                title="Copy"
                            >
                                <Icon
                                    name="copy"
                                    size={15}
                                />

                                Copy

                            </button>


                            <button
                                onClick={
                                    onRegenerate
                                }
                                title="Regenerate"
                                disabled={
                                    loading
                                }
                            >
                                <Icon
                                    name="refresh"
                                    size={15}
                                />

                                Regenerate

                            </button>

                        </div>

                    )}

            </div>


            {isUser && (
                <div className={styles["user-avatar"]}>
                    U
                </div>
            )}

        </div>
    );
}

export default AIAssistant;